import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button, CopyIcon } from "~/common/components";
import TextArea from "~/common/components/ui/Textarea";
import { copyToClipboard } from "~/common/utils";
import { GenerateAISummaryQuestionData } from "~/common/utils/consultations/shared";
import { ConsultationQuestionsLayout } from "~/patients/components/consultations/shared";
import {
  GenerateAISummaryFormValues,
  GenerateAISummarySchema,
  GenerateAISummaryValuesType,
} from "~/patients/utils/consultations/intakes";

interface GenerateAISummaryForm {
  question: GenerateAISummaryQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: GenerateAISummaryFormValues) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswers: GenerateAISummaryValuesType;
  isOptional: boolean;
  options?: string[];
}

export const GenerateAISummaryForm = ({
  completedAnswers,
  disableBack,
  handleGoBack,
  isSubmitting,
  onSubmit,
  question,
  isOptional,
  options,
}: GenerateAISummaryForm) => {
  const { title } = question;

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<GenerateAISummaryFormValues>({
    defaultValues: { answer: { ...completedAnswers } },
    resolver: zodResolver(GenerateAISummarySchema),
  });

  const prompt = options?.[0] ?? "";

  const saveAnswers: SubmitHandler<GenerateAISummaryFormValues> = (data) => {
    onSubmit({ answer: { prompt: prompt, value: data.answer.value } });
  };

  const hasValue = watch("answer.value");

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      isSubmitting={isSubmitting}
      title={title}
      disableBack={disableBack}
      submitButtonLabel={isOptional && !hasValue ? "Skip" : undefined}
    >
      <div className="flex h-full flex-col gap-3">
        <ol className="flex list-decimal flex-col gap-5 text-gray-5">
          <div className="flex flex-col gap-3">
            <li className="ml-4">Click below to copy the prompt</li>
            <Button onClick={() => prompt && copyToClipboard(prompt)}>
              <CopyIcon /> Copy prompt
            </Button>
          </div>
          <li className="ml-4">
            Log in to your AI platform, then paste and run the prompt
          </li>
          <li className="ml-4">Paste the AI response in the box below</li>
        </ol>
        <TextArea
          key="paste_ai_response"
          rows={5}
          placeholder="Paste AI response here"
          id="what_kind_of_student_were"
          {...register("answer.value")}
          error={errors.answer?.value?.message}
          className="h-full placeholder:not-italic"
          containerClassName="h-full"
          textAreaDivClassName="h-full"
          charactersCount={watch("answer.value")?.length}
          maxCharacters={5000}
        />
      </div>
    </ConsultationQuestionsLayout>
  );
};
